<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-users-class" class="q-mb-sm" /> {{ $t('planned_vacancies') }}
		</div>

		<q-table
			:data="planned_vacancies"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props">
				<q-search
					v-model="filter"
					icon="far fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>

				<div class="col" />

				<q-btn
					color="primary"
					class="q-mr-md"
					icon="far fa-file-exclamation"
					:label="$t('canceled_vacancies')"
					@click="show_enrollment_modal = true"
					:disable="disableShowCanceledVacancies"
				/>

				<q-btn
					color="secondary"
					icon="far fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'planned_vacancy', params: { id: 0 } })"
					:disable="disableEditPlannedVacancies"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td key="id" :props="props" auto-width>
					{{ props.row.id }}
				</q-td>

				<q-td key="company_branch_name" :props="props">
					{{ props.row.company_branch_name }}
				</q-td>

				<q-td key="company_branch_document" :props="props">
					{{ props.row.company_branch_document }}
				</q-td>

				<q-td key="presential_event_catalog_title" :props="props">
					{{ props.row.presential_event_catalog_title }}
				</q-td>

				<q-td key="presential_event_catalog_code" :props="props">
					{{ props.row.presential_event_catalog_code }}
				</q-td>

				<q-td key="qty_available_seats" :props="props">
					{{ props.row.qty_available_seats }}
				</q-td>

				<q-td key="qty_filled_seats" :props="props">
					{{ props.row.qty_filled_seats }}
				</q-td>

				<q-td key="year_quarter" :props="props">
					{{ props.row.year_quarter }}
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({
								name: 'planned_vacancy',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
						:disable="disableEditPlannedVacancies || props.row.qty_filled_seats > 0"
					>
						<q-tooltip v-if="props.row.qty_filled_seats > 0">
							{{ $t('cant_remove_filled_vancancy') }}
						</q-tooltip>
						<q-tooltip v-else>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<enrollment-cancellation
			v-if="show_enrollment_modal"
			:show_enrollment_modal="show_enrollment_modal"
			@hide_enrollment_modal="hideEnrollmentModal()"
		/>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { LocalStorage } from 'quasar'

export default {
	name: 'AdminPlannedVacancies',

	i18nOptions: { namespaces: ['custom', 'common', 'presential', 'admin'] },

	components: {
		'enrollment-cancellation': () =>
			import('components/admin/planned_vacancies/EnrollmentCancellation'),
	},

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			planned_vacancies: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'company_branch_name',
					required: true,
					label: 'branch',
					align: 'left',
					field: 'company_branch_name',
					sortable: false,
				},
				{
					name: 'company_branch_document',
					required: true,
					label: 'document',
					align: 'left',
					field: 'company_branch_document',
					sortable: false,
				},
				{
					name: 'presential_event_catalog_title',
					required: true,
					label: 'course_name',
					align: 'left',
					field: 'presential_event_catalog_title',
					sortable: false,
				},
				{
					name: 'presential_event_catalog_code',
					required: true,
					label: 'course_catalog_code',
					align: 'left',
					field: 'presential_event_catalog_code',
					sortable: false,
				},
				{
					name: 'qty_available_seats',
					required: true,
					label: 'total_vacancies',
					align: 'left',
					field: 'qty_available_seats',
					sortable: true,
				},
				{
					name: 'qty_filled_seats',
					required: true,
					label: 'filled_vacancies',
					align: 'left',
					field: 'qty_filled_seats',
					sortable: true,
				},
				{
					name: 'year_quarter',
					required: true,
					label: 'quarter',
					align: 'left',
					field: 'year_quarter',
					sortable: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			filter: '',
			action_id: '',
			confirm_remove: false,
			show_enrollment_modal: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			num_rows: 'datatable/num_rows',
			user_module_items: 'common/user_module_items',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		disableEditPlannedVacancies() {
			let item = this.user_module_items.find(
				(e) => e.slug === 'edit_presential_manager_seats'
			)
			if (item) return !item.permission
			else return true
		},

		disableShowCanceledVacancies() {
			let item = this.user_module_items.find((e) => e.slug === 'view_cancel_enrollments')
			if (item) return !item.permission
			else return true
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/presential_manager/admin_presential_seats/?${this.$qs.stringify(data)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.planned_vacancies = response.data.results.map((e) => {
						return {
							...e,
							actions: e.id,
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_manager_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS_PRESENTIAL', [response.data])
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(
					`/presential_manager/admin_presential_seats/${this.action_id}/`,
					this.getHeader
				)
				.then(() => {
					this.request({
						pagination: this.server_pagination,
					})

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.notify({
						message: this.$t('error_removing_item'),
						type: 'negative',
						icon: 'fal fa-ban',
					})
				})
				.finally(() => {
					this.confirm_remove = false
					this.$q.loading.hide()
				})
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		hideEnrollmentModal() {
			this.show_enrollment_modal = false
			this.request({ pagination: this.server_pagination })
		},
	},

	created() {
		this.loadPagination()
		this.fetchLists()
		this.request({ pagination: this.server_pagination })
	},
}
</script>

<style lang="stylus" scoped></style>
